import { LINKABLE_PROPERTY_PAGE_SIZE_MAX, RELATIONSHIP_PAGE_SIZE_MAX, type DocumentOwner, type DocumentRecord, type LinkableProperty, type ServicedProperty, type Vendor, type VendorService } from "@shyne/shared/api";
import { ReadAllResponse, uuid } from "@/interfaces";
import { store } from "@/lib/store.ts";
import { requirePermission } from "@/lib/permissions";
import { api } from "@/lib/api";
import { expiresOnOf } from "@/lib/documents";
import { DocumentsRepository } from "@/repositories/documents.repository";

export type { LinkableProperty, ServicedProperty, Vendor, VendorService };

export type VendorSortKey = "name" | "email";

export type VendorDetail = { vendor: Vendor };

const activeCustomer = (): uuid => {
  const customer = store.getState().organization.activeOrgId;
  if (!customer) throw new Error("Choose the customer whose vendors to manage.");
  return customer;
};

const vendorDocuments = (vendor: uuid): DocumentOwner => ({ kind: "vendor", customer: activeCustomer(), vendor });

export async function everyVendorOf(customer: uuid): Promise<Vendor[]> {
  const vendors: Vendor[] = [];
  for (let page = 1; ; page++) {
    const { items, total } = await api.vendors.list(customer, { page, pageSize: RELATIONSHIP_PAGE_SIZE_MAX });
    vendors.push(...items);
    if (items.length < RELATIONSHIP_PAGE_SIZE_MAX || vendors.length >= total) return vendors;
  }
}

export const VendorsRepository = {
  async listByCurrentOrg(
    page = 1,
    pageSize = 10,
    name?: string,
    service?: string,
    sortKey: VendorSortKey = "name",
    sortOrder: "asc" | "desc" = "asc"
  ): Promise<ReadAllResponse<Vendor>> {
    await requirePermission("vendors.view");

    const customer = store.getState().organization.activeOrgId;
    if (!customer) return { data: [], count: 0 };

    const { items, total } = await api.vendors.list(customer, {
      ...(name ? { search: name } : {}),
      ...(service ? { service } : {}),
      sort: sortKey,
      order: sortOrder,
      page,
      pageSize,
    });
    return { data: items, count: total };
  },

  async remove(vendorId: uuid): Promise<void> {
    await requirePermission("vendors.remove");
    await api.vendors.unlink(activeCustomer(), vendorId);
  },

  async getDetail(vendorId: uuid): Promise<VendorDetail> {
    await requirePermission("vendors.view");
    return { vendor: await api.vendors.get(activeCustomer(), vendorId) };
  },

  async uploadDocument(vendorId: uuid, file: File, expiresAt?: string | Date): Promise<DocumentRecord> {
    return DocumentsRepository.upload(vendorDocuments(vendorId), file, expiresOnOf(expiresAt));
  },

  async deleteDocument(docId: uuid): Promise<void> {
    await DocumentsRepository.remove(docId);
  },

  async listDocuments(vendorId: uuid, page = 1, pageSize = 10): Promise<ReadAllResponse<DocumentRecord>> {
    return DocumentsRepository.page(vendorDocuments(vendorId), page, pageSize);
  },

  async listVendorProperties(vendorId: uuid, page = 1, pageSize = 10): Promise<ReadAllResponse<ServicedProperty>> {
    await requirePermission("vendors.view");

    const { items, total } = await api.vendors.properties(activeCustomer(), vendorId, { page, pageSize });
    return { data: items, count: total };
  },

  async addVendorProperty(vendorId: uuid, propertyId: uuid): Promise<ServicedProperty> {
    await requirePermission("vendors.edit");
    return api.vendors.serviceProperty(activeCustomer(), vendorId, { property: propertyId });
  },

  async removeVendorProperty(vendorId: uuid, propertyId: uuid): Promise<void> {
    await requirePermission("vendors.edit");
    await api.vendors.stopServicingProperty(activeCustomer(), vendorId, propertyId);
  },

  async getAvailableProperties(vendorId: uuid, search?: string): Promise<LinkableProperty[]> {
    await requirePermission("vendors.view");

    const { items } = await api.vendors.linkableProperties(activeCustomer(), vendorId, {
      ...(search ? { search } : {}),
      pageSize: LINKABLE_PROPERTY_PAGE_SIZE_MAX,
    });
    return items;
  },
};
